import React from 'react';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Icon,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Switch,
  FormControlLabel,
  Typography
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { PropertyRow, ValueType, FlexiblePropertiesFormRef, FlexiblePropertiesFormProps } from './types';
import {
  buildRowsFromInitialEntries,
  buildEntriesFromRows,
  validateRows,
  generateRowId,
  coerceToType,
  normalizeKey
} from './utils';

/**
 * A flexible properties form that allows users to add any key-value pairs
 * without being restricted to predefined properties.
 *
 * This component replaces the rigid DynamicPropertiesForm by providing the ability
 * for users to freely define property keys and choose their value types (string, number, boolean).
 *
 * Features:
 * - Truly optional: no required empty rows when no initial data exists
 * - Add/remove property rows dynamically (can remove all rows)
 * - Choose value type for each property
 * - Real-time validation (duplicate keys, required fields)
 * - Keyboard navigation support (Enter to add new row)
 * - Type coercion when switching value types
 * - Compatible API with DynamicPropertiesForm
 *
 * @param initialEntries - Initial property entries to populate the form (empty = no rows)
 * @param onConfirm - Callback when entries are confirmed (optional, used with internal button)
 * @param hideConfirmButton - Whether to hide the internal confirm button (default: false)
 * @param disabled - Whether to disable the entire form (default: false)
 * @param ref - Imperative handle that exposes getEntries() method
 */
const FlexiblePropertiesForm = React.forwardRef<FlexiblePropertiesFormRef, FlexiblePropertiesFormProps>(
  ({ initialEntries = [], onConfirm, hideConfirmButton = false, disabled = false }, ref) => {
    const { t } = useTranslation();
    const [rows, setRows] = React.useState<PropertyRow[]>(() => {
      return buildRowsFromInitialEntries(initialEntries);
    });

    // Focus management refs
    const keyInputRefs = React.useRef<{ [key: string]: HTMLInputElement | null }>({});

    function createEmptyRow(): PropertyRow {
      return {
        id: generateRowId(),
        key: '',
        type: 'string',
        value: ''
      };
    }

    // Expose the getEntries method to the parent
    React.useImperativeHandle(
      ref,
      () => ({
        getEntries: () => buildEntriesFromRows(rows)
      }),
      [rows]
    );

    // Validate rows whenever they change
    React.useEffect(() => {
      const validatedRows = validateRows(rows);
      const hasChanges = validatedRows.some((validatedRow, index) => {
        const originalRow = rows[index];
        return validatedRow.keyError !== originalRow.keyError || validatedRow.valueError !== originalRow.valueError;
      });

      if (hasChanges) {
        setRows(validatedRows);
      }
    }, [rows]);

    const addRow = () => {
      const newRow = createEmptyRow();
      setRows((prev) => [...prev, newRow]);

      // Focus the new row's key input after the state update
      setTimeout(() => {
        const keyInput = keyInputRefs.current[newRow.id];
        if (keyInput) {
          keyInput.focus();
        }
      }, 0);
    };

    const removeRow = (id: string) => {
      setRows((prev) => prev.filter((row) => row.id !== id));
    };

    const updateRowKey = (id: string, key: string) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, key, keyError: undefined } : row)));
    };

    const updateRowType = (id: string, type: ValueType) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const coercedValue = coerceToType(row.value, type);
            return { ...row, type, value: coercedValue, valueError: undefined };
          }
          return row;
        })
      );
    };

    const updateRowValue = (id: string, value: string | number | boolean) => {
      setRows((prev) => prev.map((row) => (row.id === id ? { ...row, value, valueError: undefined } : row)));
    };

    const handleKeyDown = (e: React.KeyboardEvent, rowId: string) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const currentRowIndex = rows.findIndex((row) => row.id === rowId);
        const currentRow = rows[currentRowIndex];

        // Only add a new row if the current row has a valid key
        if (normalizeKey(currentRow.key)) {
          addRow();
        }
      }
    };

    const renderValueInput = (row: PropertyRow) => {
      const commonProps = {
        fullWidth: true,
        disabled,
        size: 'small' as const,
        error: !!row.valueError,
        helperText: row.valueError
      };

      switch (row.type) {
        case 'boolean':
          return (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(row.value)}
                  onChange={(e) => updateRowValue(row.id, e.target.checked)}
                  disabled={disabled}
                  color="primary"
                />
              }
              label={row.value ? 'True' : 'False'}
            />
          );

        case 'number':
          return (
            <TextField
              {...commonProps}
              type="number"
              inputMode="numeric"
              value={row.value || ''}
              onChange={(e) => {
                const numValue = parseFloat(e.target.value);
                updateRowValue(row.id, isNaN(numValue) ? e.target.value : numValue);
              }}
              onKeyDown={(e) => handleKeyDown(e, row.id)}
            />
          );

        case 'string':
        default:
          return (
            <TextField
              {...commonProps}
              value={row.value || ''}
              onChange={(e) => updateRowValue(row.id, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, row.id)}
            />
          );
      }
    };

    const handleConfirm = () => {
      const entries = buildEntriesFromRows(rows);
      onConfirm?.(entries);
    };

    const isFormValid = rows.every((row) => !row.keyError && !row.valueError);

    return (
      <Card elevation={1}>
        <CardContent>
          <Table>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('f-property-key') || 'Key'}
                      value={row.key}
                      error={!!row.keyError}
                      helperText={row.keyError}
                      disabled={disabled}
                      inputRef={(el) => {
                        keyInputRefs.current[row.id] = el;
                      }}
                      onChange={(e) => updateRowKey(row.id, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, row.id)}
                    />
                  </TableCell>

                  <TableCell>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel>{t('f-type') || 'Type'}</InputLabel>
                      <Select value={row.type} onChange={(e) => updateRowType(row.id, e.target.value as ValueType)}>
                        <MenuItem value="string">{t('f-type-string') || 'String'}</MenuItem>
                        <MenuItem value="number">{t('f-type-number') || 'Number'}</MenuItem>
                        <MenuItem value="boolean">{t('f-type-boolean') || 'Boolean'}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>

                  <TableCell>{renderValueInput(row)}</TableCell>

                  <TableCell>
                    <IconButton
                      onClick={() => removeRow(row.id)}
                      disabled={disabled}
                      title={t('f-remove-property') || 'Remove property'}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="textSecondary">
                      {t('f-no-properties') || 'No properties added'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item>
              <Button variant="outlined" startIcon={<Icon>add</Icon>} onClick={addRow} disabled={disabled}>
                {t('f-add-property') || 'Add Property'}
              </Button>
            </Grid>

            {!hideConfirmButton && (
              <Grid item>
                <Button variant="contained" color="primary" onClick={handleConfirm} disabled={disabled || !isFormValid}>
                  {t('f-confirm') || 'Confirm'}
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    );
  }
);

FlexiblePropertiesForm.displayName = 'FlexiblePropertiesForm';
export default FlexiblePropertiesForm;
